import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ArrowRight, RotateCcw } from 'lucide-react';
import { Task } from '../types';
import { TaskCard } from './TaskCard';
import { ProgressBar } from './ProgressBar';
interface ScoringProps {
  tasks: Task[];
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  onComplete: () => void;
  onRestart: () => void;
}
export function Scoring({
  tasks,
  setTasks,
  onComplete,
  onRestart
}: ScoringProps) {
  const handleUpdate = (id: string, updates: Partial<Task>) => {
    setTasks(tasks.map(t => t.id === id ? {
      ...t,
      ...updates
    } : t));
  };
  const handleDelete = (id: string) => {
    setTasks(tasks.filter(t => t.id !== id));
  };
  const handleAddCustom = () => {
    const newTask: Task = {
      id: Math.random().toString(36).substring(7),
      name: 'New Custom Task',
      revenueImpact: null,
      timeSpent: 0,
      repeatability: null,
      isCustom: true
    };
    setTasks([...tasks, newTask]);
  };
  const completedTasksCount = tasks.filter(t => t.revenueImpact !== null && t.repeatability !== null && t.timeSpent > 0).length;
  const canProceed = completedTasksCount >= 3;
  return <div className="min-h-screen bg-cream pb-32" data-id="element-205">
      <ProgressBar tasks={tasks} data-id="element-206" />

      <div className="max-w-3xl mx-auto px-6 pt-12" data-id="element-207">
        <div className="mb-10 text-center" data-id="element-208">
          <h2 className="text-4xl font-extrabold text-ink mb-4" data-id="element-209">
            Score your tasks
          </h2>
          <p className="text-lg text-ink/70 font-medium" data-id="element-210">
            Rate these common marketing tasks, or add your own. We need at least
            3 fully scored tasks to build your plan.
          </p>
          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={onRestart} className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white border-2 border-ink/10 text-ink/60 font-bold text-sm hover:border-ink/30 hover:text-ink transition-colors" data-id="element-211">
            <RotateCcw size={14} strokeWidth={3} data-id="element-212" />
            Start over
          </motion.button>
        </div>

        <div className="flex flex-col gap-6" data-id="element-213">
          <AnimatePresence mode="popLayout" data-id="element-214">
            {tasks.map(task => <TaskCard key={task.id} task={task} onUpdate={handleUpdate} onDelete={handleDelete} data-id="element-215" />)}
          </AnimatePresence>
        </div>

        <motion.button whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} onClick={handleAddCustom} className="w-full mt-8 py-6 rounded-3xl border-4 border-dashed border-ink/10 text-ink/50 font-bold text-lg flex items-center justify-center gap-2 hover:border-ink/30 hover:text-ink hover:bg-ink/5 transition-all" data-id="element-216">
          <Plus size={24} data-id="element-217" />
          Add custom task
        </motion.button>
      </div>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 w-full p-6 bg-gradient-to-t from-cream via-cream to-transparent pointer-events-none flex justify-center z-50" data-id="element-218">
        <motion.button initial={{
        y: 100
      }} animate={{
        y: 0
      }} whileHover={canProceed ? {
        scale: 1.05,
        y: -4
      } : {}} whileTap={canProceed ? {
        scale: 0.95
      } : {}} onClick={canProceed ? onComplete : undefined} disabled={!canProceed} className={`pointer-events-auto px-10 py-5 rounded-full font-bold text-xl flex items-center gap-3 shadow-soft-lg transition-all ${canProceed ? 'bg-grape text-white hover:shadow-soft cursor-pointer' : 'bg-ink/10 text-ink/40 cursor-not-allowed shadow-none'}`} data-id="element-219">
          See my results
          <ArrowRight size={24} data-id="element-220" />
        </motion.button>
      </div>
    </div>;
}